import { computed, ref } from 'vue';

export const usePayPalSession = () => {
    const PAYPAL_KEYS = {
        CHECKOUT_TOKEN: 'paypalCheckoutToken',
        ORDER_ID: 'paypalOrderId',
        PRODUCT_INFO: 'paypalCheckoutProduct'
    } as const;

    const isPayPalCheckout = computed(() => {
        // Only consider it a PayPal checkout if we have all required data
        const hasPayPalToken = sessionStorage.getItem(PAYPAL_KEYS.CHECKOUT_TOKEN) !== null;
        const hasPayPalOrderId = sessionStorage.getItem(PAYPAL_KEYS.ORDER_ID) !== null;
        const hasPayPalProduct = sessionStorage.getItem(PAYPAL_KEYS.PRODUCT_INFO) !== null;
        
        return hasPayPalToken && hasPayPalOrderId && hasPayPalProduct;
    });

    const getPayPalData = () => {
        if (!isPayPalCheckout.value) {
            return null;
        }

        try {
            const productData = sessionStorage.getItem(PAYPAL_KEYS.PRODUCT_INFO);
            const parsedProductData = productData ? JSON.parse(productData) : null;

            return {
                token: sessionStorage.getItem(PAYPAL_KEYS.CHECKOUT_TOKEN),
                orderId: sessionStorage.getItem(PAYPAL_KEYS.ORDER_ID),
                productInfo: parsedProductData
            };
        } catch (error) {
            console.error('[usePayPalSession] Failed to parse PayPal data:', error);
            clearPayPalData();
            return null;
        }
    };

    const setPayPalData = (data: {
        token: string;
        orderId?: string;
        productInfo: any;
    }) => {
        try {
            sessionStorage.setItem(PAYPAL_KEYS.CHECKOUT_TOKEN, data.token);
            sessionStorage.setItem(PAYPAL_KEYS.PRODUCT_INFO, JSON.stringify(data.productInfo));
            
            if (data.orderId) {
                sessionStorage.setItem(PAYPAL_KEYS.ORDER_ID, data.orderId);
            }
        } catch (error) {
            console.error('[usePayPalSession] Failed to set PayPal data:', error);
        }
    };

    const clearPayPalData = () => {
        Object.values(PAYPAL_KEYS).forEach(key => {
            sessionStorage.removeItem(key);
        });
    };

    const validatePayPalSession = () => {
        // Check if we have a valid PayPal session
        if (!isPayPalCheckout.value) {
            // Clear any partial data
            clearPayPalData();
            return false;
        }

        // Validate that the data is not too old (optional - you can add timestamp validation)
        return true;
    };

    const trackPayPalOrder = (orderId: string) => {
        try {
            // Store the Shopware order ID for tracking
            sessionStorage.setItem('paypalShopwareOrderId', orderId);
        } catch (error) {
            console.error('[usePayPalSession] Failed to track PayPal order:', error);
        }
    };

    const getTrackedOrderId = () => {
        return sessionStorage.getItem('paypalShopwareOrderId');
    };

    const clearStalePayPalData = () => {
        // Clear PayPal data if we're not in a proper PayPal checkout flow
        if (!isPayPalCheckout.value) {
            clearPayPalData();
        }
    };

    return {
        isPayPalCheckout,
        getPayPalData,
        setPayPalData,
        clearPayPalData,
        validatePayPalSession,
        trackPayPalOrder,
        getTrackedOrderId,
        clearStalePayPalData,
        PAYPAL_KEYS
    };
}; 